import { Helmet } from "react-helmet-async";
import { Container, Drawer, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { Course, Lesson } from "../api/course-service";
import CourseService, { coverUrl } from "../api/course-service";
import type { UserProgress } from "../api/user-progress-service";
import { UserProgressService } from "../api/user-progress-service";
import type { MetroStop } from "../utils/metro-stops";
import { metroStops } from "../utils/metro-stops";

import ENV from "../env";
import WorkoutService from "../api/workout-service";
import { lessonProgress } from "../utils/course-progress";
import { CourseMetroHeader } from "../components/courses/metro/CourseMetroHeader";
import { MetroLegend } from "../components/courses/metro/MetroLegend";
import { MetroMap } from "../components/courses/metro/MetroMap";
import { LessonDetails } from "../components/courses/LessonDetails";

export default function CoursePage() {
    const [ course, setCourse ] = useState<Course | null>(null);
    const [ userProgress, setUserProgress ] = useState<UserProgress | null>(null);
    const [ verifyLoading, setVerifyLoading ] = useState(false);

    // The selection is kept by id so it survives a reload of the course
    const [ openLesson, setOpenLesson ] = useState<string | null>(null);

    const { id } = useParams();
    const navigate = useNavigate();

    const stops = course ? metroStops(course, userProgress) : [];
    const selectedStop = stops.find((stop) => stop.lesson.id === openLesson);
    const selectedLesson = selectedStop?.lesson;

    function lessonSwitch(lesson: Lesson) {
        setOpenLesson(lesson.id === openLesson ? null : lesson.id);
    }

    function handleStart(stop: MetroStop) {
        setOpenLesson(stop.lesson.id);
    }

    function startVerify(session: number) {
        if (!course || !selectedLesson) return;

        setVerifyLoading(true);

        console.log("Start verification session " + session + " for lesson: " + selectedLesson.id);
        // The lesson decides the questions, whatever topic they are stored in; the course records the progress
        WorkoutService.startNewWorkout(selectedLesson.title, 7, [], [ selectedLesson.id ], [], session, course.id)
            .then(response => {
                if (!response.id) {
                    alert("Error: Sorry it looks like currently we are not able to start workout for you! Please try again later.");
                    return;
                }

                console.log("Workout started: " + response.id);
                navigate('/workouts/' + response.id);
            })
            .catch(reason => {
                console.error("Unable to start workout: ", reason);
            })
            .finally(() => {
                setVerifyLoading(false);
            });
    }

    useEffect(() => {
        if (!id) return;

        CourseService.course(id)
            .then(loaded => setCourse(loaded))
            .catch(reason => {
                console.error("Unable to load course " + id + ": ", reason);
                navigate("/404");
            });
    }, [ id, navigate ]);

    useEffect(() => {
        if (!id) return;

        UserProgressService.userProgress(id)
            .then(data => setUserProgress(data))
            .catch(reason => console.error("Unable to load progress of " + id + ": ", reason));
    }, [ id ]);

    // The effect above sends unknown course ids to /404; render nothing until the course is there
    if (!course) return <></>;

    const units = course.groups
        .filter((group) => course.lessons.some((lesson) => lesson.groupId === group.id))
        .map((group) => group.title);

    return <>
        <Helmet>
            <title>Course: { course.title } | { ENV.APP_NAME }</title>
        </Helmet>

        <Container maxWidth="lg">
            <Stack spacing={ 3 }>
                <CourseMetroHeader title={ course.title } cover={ coverUrl(course) } stops={ stops } onStart={ handleStart }/>

                <MetroLegend units={ units }/>

                <MetroMap course={ course } progress={ userProgress } stops={ stops }
                          selectedLessonId={ selectedLesson?.id } onSelect={ lessonSwitch }/>
            </Stack>
        </Container>

        <Drawer anchor="right" open={ !!selectedLesson } onClose={ () => setOpenLesson(null) }
                PaperProps={ { sx: { width: { xs: '100%', sm: 440 } } } }>
            { selectedLesson && (
                <LessonDetails courseId={ course.id } lesson={ selectedLesson } caption={ selectedStop?.caption }
                               progress={ lessonProgress(userProgress, selectedLesson) }
                               verifyLoading={ verifyLoading } onVerify={ startVerify }
                               onClose={ () => setOpenLesson(null) }/>
            ) }
        </Drawer>
    </>;
}
